import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Phone, Mail, Trash2, Check, RefreshCw, Inbox } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import AdminBlog from "@/components/admin/AdminBlog";
import AdminGsc from "@/components/admin/AdminGsc";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { setSeo } from "@/lib/seo";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, nouveau: 0, traite: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get("/leads"),
        api.get("/leads/stats"),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Impossible de charger les demandes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSeo({ title: "Tableau de bord — Admin", noindex: true });
    if (user === false) return nav("/admin/login");
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggleStatus = async (lead) => {
    const next = lead.status === "nouveau" ? "traité" : "nouveau";
    try {
      await api.patch(`/leads/${lead.id}`, { status: next });
      toast.success(`Marqué comme ${next}`);
      load();
    } catch (e) {
      toast.error("Échec de la mise à jour.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Demande supprimée.");
      load();
    } catch (e) {
      toast.error("Échec de la suppression.");
    }
  };

  const doLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  if (user === null)
    return (
      <div className="min-h-screen grid place-items-center bg-brand-mist text-slate-500">
        Chargement…
      </div>
    );

  return (
    <div className="min-h-screen bg-brand-mist text-brand-navy">
      <header className="border-b border-brand-line bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-10 h-10 rounded-lg bg-brand-navy text-white font-display font-bold">
              S
            </span>
            <span className="font-display font-semibold tracking-tight text-sm">
              TABLEAU DE BORD
              <span className="block text-[10px] tracking-[0.28em] text-brand-gold">
                DEMANDES DE DEVIS
              </span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={load}
              data-testid="refresh-btn"
              className="text-slate-500 hover:text-brand-navy transition-colors duration-300"
              aria-label="Rafraîchir"
            >
              <RefreshCw className={"w-5 h-5 " + (loading ? "animate-spin" : "")} />
            </button>
            <button
              onClick={doLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 text-xs uppercase tracking-wider font-medium text-slate-500 hover:text-brand-navy transition-colors duration-300"
            >
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <Tabs defaultValue="leads" data-testid="admin-tabs">
          <TabsList className="mb-8 bg-white border border-brand-line">
            <TabsTrigger value="leads" data-testid="tab-leads">
              Demandes de devis
            </TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog">
              Articles SEO
            </TabsTrigger>
            <TabsTrigger value="gsc" data-testid="tab-gsc">
              Performances
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[
            { label: "Total", value: stats.total, cls: "text-brand-navy" },
            { label: "Nouveaux", value: stats.nouveau, cls: "text-brand-golddark" },
            { label: "Traités", value: stats.traite, cls: "text-emerald-600" },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded-2xl border border-brand-line bg-white p-6 shadow-sm"
              data-testid={`stat-${s.label.toLowerCase()}`}
            >
              <div className={"font-display font-semibold text-4xl " + s.cls}>
                {s.value}
              </div>
              <div className="text-xs uppercase tracking-[0.16em] text-slate-500 mt-1 font-medium">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-brand-line bg-white overflow-hidden shadow-sm">
          {leads.length === 0 && !loading ? (
            <div className="py-20 flex flex-col items-center gap-4 text-slate-400">
              <Inbox className="w-10 h-10" />
              Aucune demande pour le moment.
            </div>
          ) : (
            <Table data-testid="leads-table">
              <TableHeader>
                <TableRow className="border-brand-line hover:bg-transparent">
                  <TableHead className="text-slate-500">Client</TableHead>
                  <TableHead className="text-slate-500">Contact</TableHead>
                  <TableHead className="text-slate-500">Ville</TableHead>
                  <TableHead className="text-slate-500">Service</TableHead>
                  <TableHead className="text-slate-500">Message</TableHead>
                  <TableHead className="text-slate-500">Statut</TableHead>
                  <TableHead className="text-slate-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((l) => (
                  <TableRow
                    key={l.id}
                    data-testid={`lead-row-${l.id}`}
                    className="border-brand-line hover:bg-brand-mist/60"
                  >
                    <TableCell className="font-medium text-brand-navy">
                      {l.name}
                      <div className="text-xs text-slate-400">
                        {new Date(l.created_at).toLocaleString("fr-FR")}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-700">
                      <a
                        href={`tel:${l.phone}`}
                        className="flex items-center gap-1.5 hover:text-brand-golddark transition-colors duration-300"
                      >
                        <Phone className="w-3.5 h-3.5" /> {l.phone}
                      </a>
                      {l.email && (
                        <a
                          href={`mailto:${l.email}`}
                          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-brand-navy transition-colors duration-300 mt-1"
                        >
                          <Mail className="w-3 h-3" /> {l.email}
                        </a>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-700">{l.city || "—"}</TableCell>
                    <TableCell className="text-slate-700">{l.service || "—"}</TableCell>
                    <TableCell className="text-slate-500 max-w-[220px] truncate">
                      {l.message || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          "rounded-full border-0 " +
                          (l.status === "nouveau"
                            ? "bg-brand-gold text-brand-navy"
                            : "bg-emerald-100 text-emerald-700")
                        }
                      >
                        {l.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => toggleStatus(l)}
                          data-testid={`toggle-${l.id}`}
                          className="p-2 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors duration-300"
                          aria-label="Changer le statut"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => remove(l.id)}
                          data-testid={`delete-${l.id}`}
                          className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors duration-300"
                          aria-label="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
          </TabsContent>

          <TabsContent value="blog">
            <AdminBlog />
          </TabsContent>

          <TabsContent value="gsc">
            <AdminGsc />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
